import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Box from './Box';
import { Paragraph } from './Paragraph';

import CustomIcon from '../Icons/CustomIcon';

import { useOnClickOutside } from '../../../utils/hooks/useOnClickOutside';

type TProps = {
    placeholder?: string;
    items?: any[];
    size?: 'sm';
    variant?: 'secondary',
    handleSelection: (a: any) => void;
};

type TStyledWrapper = {
    open?: boolean;
    size?: 'sm';
    variant?: 'secondary',
};

type TStyledButton = {
    isLast?: boolean;
    itemIsSelected?: boolean;
    size?: 'sm';
    variant?: 'secondary',
};

const StyledWrapper = styled(Box) <TStyledWrapper>`
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    padding: ${({ size }) => (size === "sm" ? '16px 12px' : '20px 16px')};
    background: ${({ theme, open, variant }) => (open ? theme.color['neutral2'] : variant === 'secondary' ? 'white' : theme.color['neutral1'])};
    border-radius: ${({ open }) => (open ? '8px 8px 0px 0px' : '8px')};
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.color['neutral2']};
    }

    &:focus-visible {
        outline: none;
        border: 3px solid ${({ theme }) => theme.color['blue-1']};
    }
`;

const Ul = styled.ul<TStyledWrapper>`
    position: absolute;
    top: ${({ size }) => (size === "sm" ? '49px' : '57px')};
    width: 100%;
    max-height: 240px;
    overflow: auto;
    background: ${({ theme }) => theme.color['neutral1']};
    border-radius: 0px 0px 8px 8px;
    list-style: none;
`;

const StyledButton = styled.button<TStyledButton>`
    width: 100%;
    padding: ${({ size }) => (size === "sm" ? '16px 12px' : '20px 16px')};
    border: none;
    background-color: ${({ theme }) => theme.color['neutral2']};
    color: ${({ itemIsSelected, theme }) => (itemIsSelected ? theme.color['blue-4'] : theme.color['coolgray-12'])};
    border-radius: ${({ isLast }) => (isLast ? ' 0px 0px 8px 8px' : null)};
    text-align: start;
    cursor: pointer;

    &:hover {
        background-color: ${({ itemIsSelected, theme }) => (itemIsSelected ? theme.color['neutral2'] : 'black')};
        color: ${({ itemIsSelected, theme }) => (itemIsSelected ? theme.color['blue-4'] : 'white')};
        cursor: ${({ itemIsSelected }) => (itemIsSelected ? 'default' : 'pointer')};;
    }

    &:focus-visible {
        outline: none;
        border: 3px solid ${({ theme }) => theme.color['blue-1']};
    }
`;

export const SimpleDropdown = ({ placeholder, items, variant, size, handleSelection }: TProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(placeholder);
    const [selection, setSelection] = useState<any[]>([]);

    useEffect(() => {
        // If placeholder is same as one of the item values
        const matchedSelection = items?.find((item) => item.value === placeholder);
        setSelection(matchedSelection ? [matchedSelection] : []);
        setSelectedItem(matchedSelection?.value ?? placeholder);
    }, [items, placeholder]);

    useOnClickOutside(ref, () => setOpen(false));

    const handleOnClick = (item: any) => {
        if (!selection.some((current) => current.id === item.id)) {
            setSelection([item]);
            setSelectedItem(item.value);
            handleSelection({ value: item.value, id: item.id });
        }

        // Used to remove selection on second click
        //
        //  else {
        //     let selectionAfterRemoval = selection;
        //     selectionAfterRemoval = selectionAfterRemoval.filter((current) => current.id !== item.id);
        //     setSelection(selectionAfterRemoval);
        // }

        setOpen(false);
    };

    const toggle = (open: boolean) => setOpen(open);

    return (
        <Box position="relative" zIndex={10} ref={ref}>
            <StyledWrapper
                size={size}
                open={open}
                tabIndex={0}
                role="button"
                variant={variant}
                onClick={() => toggle(!open)}
                onKeyPress={() => toggle(!open)}
            >
                <Paragraph typography="ui-m" customColor={open ? 'coolgray-12' : 'coolgray-11'}>
                    {selectedItem}
                </Paragraph>
                <CustomIcon icon={open ? 'ChevronUp_xs' : 'ChevronDown_xs'} customColor="blue-4" />
            </StyledWrapper>

            {open && (
                <Ul size={size}>
                    {items?.map((item, id) => {
                        const isLast = items.length - 1 === id;
                        const itemIsSelected = selection?.find((current) => current.id === item.id);

                        return (
                            <li key={item.id}>
                                <StyledButton
                                    type="button"
                                    size={size}
                                    isLast={isLast}
                                    itemIsSelected={itemIsSelected}
                                    onClick={() => handleOnClick(item)}
                                >
                                    <Box as="span" typography="ui-m">
                                        {item.value}
                                    </Box>
                                </StyledButton>
                            </li>
                        );
                    })}
                </Ul>
            )}
        </Box>
    );
};
